// Custom JavaScript

/** Synchronize public dispatch properties before the standard cart checkout. */
class MchDispatchCheckout {
	static busy = false;

	/** Fetch same-origin cart data with a bounded network timeout. */
	static async request(url, options = {}) {
		const controller = new AbortController();
		const timeout = setTimeout(() => controller.abort(), 15000);
		try {
			const response = await fetch(url, {
				...options,
				credentials: 'same-origin',
				cache: 'no-store',
				signal: controller.signal
			});
			if (!response.ok) throw new Error(`Cart request failed (${response.status}).`);
			return await response.text();
		} finally {
			clearTimeout(timeout);
		}
	}

	/** Normalize Liquid property pairs and Ajax property objects without losing values. */
	static normalizeProperties(value) {
		if (value === null) return {};
		if (Array.isArray(value)) {
			if (!value.every((pair) => Array.isArray(pair) && pair.length === 2 && typeof pair[0] === 'string')) {
				throw new Error('Invalid dispatch property pairs.');
			}
			return Object.fromEntries(value);
		}
		if (typeof value !== 'object') throw new Error('Invalid dispatch properties.');
		return value;
	}

	/** Read server-rendered stock rules, keys and existing properties together. */
	static async readLines() {
		const url = new URL(FoxTheme.routes.cart_url, window.location.origin);
		url.searchParams.set('section_id', 'mch--dispatch-data');
		const html = await this.request(url);
		const parsed = new DOMParser().parseFromString(html, 'text/html');
		const root = parsed.querySelector('[data-mch-dispatch-data]');
		if (!root) throw new Error('Dispatch cart data is unavailable.');
		return Array.from(root.querySelectorAll('[data-mch-dispatch-item]'), (node) => {
			const properties = this.normalizeProperties(JSON.parse(node.dataset.properties || '{}'));
			const quantity = Number(node.dataset.quantity);
			if (!node.dataset.key || !Number.isSafeInteger(quantity) || quantity < 1) {
				throw new Error('Invalid dispatch cart data.');
			}
			return { key: node.dataset.key, quantity, properties, dispatch: node.dataset.dispatch || '' };
		});
	}

	/** Re-read after every write because properties can change keys and merge lines. */
	static async synchronize() {
		let lines = await this.readLines();
		const limit = lines.length * 2 + 5;
		for (let attempt = 0; attempt < limit; attempt++) {
			const item = lines.find((line) => (line.properties.Dispatch || '') !== line.dispatch);
			if (!item) return lines.length;
			const result = JSON.parse(await this.request(FoxTheme.routes.cart_change_url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({
					id: item.key,
					quantity: item.quantity,
					properties: { ...item.properties, Dispatch: item.dispatch }
				})
			}));
			if (result.errors || !Array.isArray(result.items)) throw new Error('Dispatch update failed.');
			lines = await this.readLines();
		}
		throw new Error('The cart kept changing during dispatch synchronization.');
	}

	/** Let the theme finish its 300 ms quantity debounce and pending cart requests. */
	static async waitForCart() {
		await new Promise((resolve) => setTimeout(resolve, 350));
		const deadline = Date.now() + 15000;
		while (document.querySelector('cart-items .btn--loading, gift-wrap .btn--loading, gift-wrapping .btn--loading')) {
			if (Date.now() > deadline) throw new Error('Cart update timed out.');
			await new Promise((resolve) => setTimeout(resolve, 100));
		}
		for (const input of document.querySelectorAll('cart-items input[name="updates[]"]')) {
			if (input.value === '' || !input.checkValidity()) throw new Error('Please correct the cart quantity.');
		}
	}

	/** Save cart notes and attributes that native cart submission would preserve. */
	static async saveFields(source) {
		const fields = new FormData();
		for (const [key, value] of source) {
			if (key === 'note' || /^attributes\[[^\]]+\]$/.test(key)) fields.append(key, value);
		}
		if (!Array.from(fields.keys()).length) return;
		const result = JSON.parse(await this.request(FoxTheme.routes.cart_update_url, {
			method: 'POST', body: fields, headers: { Accept: 'application/json' }
		}));
		if (result.errors || !Array.isArray(result.items)) throw new Error('Cart fields could not be saved.');
	}

	/** Freeze cart controls during preparation and restore their previous state. */
	static lock(locked) {
		for (const container of document.querySelectorAll('main-cart, cart-drawer')) {
			if (locked) {
				container.dataset.mchDispatchInert = String(container.inert);
				container.inert = true;
				container.setAttribute('aria-busy', 'true');
			} else {
				container.inert = container.dataset.mchDispatchInert === 'true';
				delete container.dataset.mchDispatchInert;
				container.removeAttribute('aria-busy');
			}
		}
	}

	/** Attempt dispatch synchronization without blocking checkout on failure. */
	static async onSubmit(event) {
		const button = event.submitter;
		const form = event.target;
		if (!(form instanceof HTMLFormElement) || button?.name !== 'checkout') return;
		const action = new URL(form.action, window.location.origin);
		const cartUrl = new URL(FoxTheme.routes.cart_url, window.location.origin);
		if (action.origin !== cartUrl.origin || action.pathname !== cartUrl.pathname) return;
		event.preventDefault();
		event.stopImmediatePropagation();
		if (MchDispatchCheckout.busy) return;
		MchDispatchCheckout.busy = true;
		// Capture unsaved notes before a pending quantity render replaces the form.
		const fields = new FormData(form);
		document.getElementById('MchDispatchError')?.remove();
		MchDispatchCheckout.lock(true);
		try {
			await MchDispatchCheckout.waitForCart();
			await MchDispatchCheckout.saveFields(fields);
			await MchDispatchCheckout.synchronize();
		} catch (error) {
			console.warn('Dispatch synchronization skipped; continuing to checkout:', error);
		} finally {
			MchDispatchCheckout.lock(false);
			MchDispatchCheckout.busy = false;
		}
		const checkout = new URL('checkout', new URL(FoxTheme.routes.root_url, window.location.origin));
		window.location.assign(checkout.href);
	}
}

document.addEventListener('submit', MchDispatchCheckout.onSubmit, true);
/** Expand the mega menu to fit the complete nested flyout without scrolling. */
function positionMegaSubmenu(event) {
	if (!(event.target instanceof Element)) return;
	const item = event.target.closest('.has-level-4');
	if (!item) return;
	const submenu = item.querySelector(':scope > .mega-menu__item-child-level-4');
	const menu = item.closest('.mega-menu');
	const wrapper = item.closest('.mega-menu__wrapper');
	if (!submenu || !menu || !wrapper) return;
	menu.classList.add('mch--submenu-expanded');
	const itemRect = item.getBoundingClientRect();
	const top = wrapper.getBoundingClientRect().top + 12;
	submenu.style.setProperty('--submenu-top', `${top - itemRect.top - item.clientTop}px`);
	const height = submenu.getBoundingClientRect().height;
	wrapper.style.setProperty('--submenu-menu-height', `${height + 24}px`);
}

/** Collapse the menu when the pointer or keyboard focus leaves a flyout. */
function resetMegaSubmenu(event) {
	if (!(event.target instanceof Element)) return;
	const item = event.target.closest('.has-level-4');
	const menu = item?.closest('.mega-menu.mch--submenu-expanded');
	if (!menu || (event.relatedTarget instanceof Node && item.contains(event.relatedTarget))) return;
	if (event.type !== 'focusout' && item.contains(document.activeElement)) return;
	menu.querySelector('.mega-menu__wrapper')?.style.removeProperty('--submenu-menu-height');
}

document.addEventListener('pointerover', positionMegaSubmenu);
document.addEventListener('focusin', positionMegaSubmenu);
document.addEventListener('pointerout', resetMegaSubmenu);
document.addEventListener('focusout', resetMegaSubmenu);

document.addEventListener('DOMContentLoaded', function() {
  document.body.addEventListener('click', function(e) {
    const link = e.target.closest('menu-sidebar summary a');
    if (link && link.href) {
      // The theme's default header.js prevents default on the summary tag.
      // This listener catches the bubbled event and forces the navigation.
      window.location.href = link.href;
    }
  });
});


// Support for legacy accordion content with toggle_visibility and .coll
window.toggle_visibility = function (id) {
  const e = document.getElementById(id);
  const ex = document.getElementById(id + 'x');
  if (!e) return;
  const isHidden = window.getComputedStyle(e).display === 'none';
  e.style.display = isHidden ? 'block' : 'none';
  if (ex) ex.textContent = isHidden ? '−' : '+';
};
document.addEventListener('click', function (e) {
  const collHeader = e.target.closest('.coll');
  if (!collHeader) return;
  const parentLi = collHeader.closest('li') || collHeader.parentElement;
  if (!parentLi) return;
  const content = parentLi.querySelector('div[id^="id"]') || collHeader.nextElementSibling;
  const iconSpan = collHeader.querySelector('span[id$="x"]');
  if (content) {
    const isHidden = window.getComputedStyle(content).display === 'none';
    content.style.display = isHidden ? 'block' : 'none';
    if (iconSpan) {
      iconSpan.textContent = isHidden ? '−' : '+';
    }
  }
});

// =====================================================
// MCH Availability, Auto-Backfill & Virtual Pagination
// =====================================================
(function () {
  const MAX_FULL_SCAN_PAGES = 12; // Up to ~336 products (12 * 28)
  const FETCH_CONCURRENCY = 4;
  const pageCache = new Map();
  let activeRunId = 0;

  const CARET_LEFT_SVG =
    '<svg class="icon icon-caret-left rtl-flip-x icon--extra-small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 16.25L6.25 10L12.5 3.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const CARET_RIGHT_SVG =
    '<svg class="icon icon-caret-right rtl-flip-x icon--extra-small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 16.25L13.75 10L7.5 3.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function getBaseCacheKey(urlObj) {
    const u = new URL(urlObj ? urlObj.toString() : window.location.href, window.location.origin);
    u.searchParams.delete('page');
    u.searchParams.delete('section_id');
    const entries = Array.from(u.searchParams.entries()).sort(function (a, b) {
      return a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]);
    });
    const qs = new URLSearchParams(entries).toString();
    return u.pathname + (qs ? '?' + qs : '');
  }

  function buildPageHref(pageNum) {
    const u = new URL(window.location.href, window.location.origin);
    u.searchParams.delete('section_id');
    if (pageNum <= 1) {
      u.searchParams.delete('page');
    } else {
      u.searchParams.set('page', String(pageNum));
    }
    return u.pathname + (u.search ? u.search : '');
  }

  function isColumnHiddenOos(col) {
    if (!col) return false;
    return (
      col.getAttribute('data-mch-hidden-oos') === 'true' ||
      col.classList.contains('mch-product-oos') ||
      Boolean(col.querySelector('.product-card[data-mch-hidden-oos="true"]'))
    );
  }

  function getColumnKey(col, fallbackIndex) {
    const pid = col.getAttribute('data-product-id');
    if (pid) return 'pid:' + pid;
    const link = col.querySelector('a[href]');
    if (link) return 'href:' + link.getAttribute('href');
    return 'idx:' + fallbackIndex;
  }

  function extractColumnsFromList(listEl) {
    const visibleItems = [];
    const hiddenKeys = [];
    const promoBlocks = [];
    if (!listEl) return { visibleItems: visibleItems, hiddenKeys: hiddenKeys, promoBlocks: promoBlocks };

    const children = Array.from(listEl.children);
    children.forEach(function (col, idx) {
      if (!col.classList.contains('f-column')) return;
      if (col.classList.contains('image-column')) {
        promoBlocks.push({ index: idx, html: col.outerHTML });
        return;
      }
      const key = getColumnKey(col, idx);
      if (isColumnHiddenOos(col)) {
        hiddenKeys.push(key);
      } else {
        visibleItems.push({ key: key, html: col.outerHTML });
      }
    });

    return {
      visibleItems: visibleItems,
      hiddenKeys: hiddenKeys,
      promoBlocks: promoBlocks
    };
  }

  function updateProductCountDisplay(totalHidden, visibleOnPageCount, knownTotalVisible) {
    const productCountEl = document.getElementById('ProductCount');
    if (!productCountEl) return;

    if (!productCountEl.dataset.mchOriginalText) {
      productCountEl.dataset.mchOriginalText = productCountEl.textContent;
    }

    const listEl = document.getElementById('ProductsList');
    const origText = productCountEl.dataset.mchOriginalText.trim();
    const allNums = origText.match(/\b(\d+)\b/g);
    if (!allNums || allNums.length === 0) return;

    const rawTotal =
      listEl && listEl.dataset.totalItems
        ? parseInt(listEl.dataset.totalItems, 10)
        : parseInt(allNums[allNums.length - 1], 10);

    const finalTotal =
      typeof knownTotalVisible === 'number'
        ? Math.max(0, knownTotalVisible)
        : Math.max(0, rawTotal - totalHidden);

    // Preserve search result phrasing ("X results for ...") if on search page
    if (/\bresults?\b/i.test(origText)) {
      productCountEl.textContent = origText.replace(/\b\d+\b/, String(finalTotal));
      return;
    }

    if (finalTotal === 0) {
      productCountEl.textContent = '0 products';
    } else if (finalTotal === 1) {
      productCountEl.textContent = '1 product';
    } else if (finalTotal > visibleOnPageCount && visibleOnPageCount > 0) {
      productCountEl.textContent = visibleOnPageCount + ' of ' + finalTotal + ' products';
    } else {
      productCountEl.textContent = finalTotal + ' products';
    }
  }

  function syncEmptyState(visibleCount) {
    const emptyState = document.querySelector('#ProductGridContainer .empty-state, .empty-state');
    if (!emptyState) return;
    if (visibleCount === 0) {
      emptyState.classList.remove('hidden');
    } else {
      emptyState.classList.add('hidden');
    }
  }

  function ensureInsertedCardsReady(listEl) {
    if (!listEl) return;
    const cards = listEl.querySelectorAll('.product-card');
    const gridClass = listEl.dataset.gridClass;
    const listClass = listEl.dataset.listClass;
    const activeLayoutBtn = document.querySelector('layout-switcher button.btn--active[data-layout-mode]');
    const layoutMode = activeLayoutBtn ? activeLayoutBtn.dataset.layoutMode : null;

    cards.forEach(function (card) {
      card.style.opacity = '1';
      if (layoutMode === 'list') {
        if (gridClass) card.classList.remove(gridClass);
        if (listClass) card.classList.add(listClass);
      } else if (layoutMode === 'grid') {
        if (listClass) card.classList.remove(listClass);
        if (gridClass) card.classList.add(gridClass);
      }
    });
  }

  function computePaginationParts(current, total) {
    if (total <= 7) {
      const all = [];
      for (let i = 1; i <= total; i++) all.push(i);
      return all;
    }
    const pages = new Set([1, total, current, current - 1, current + 1]);
    if (current <= 3) {
      pages.add(2);
      pages.add(3);
      pages.add(4);
    }
    if (current >= total - 2) {
      pages.add(total - 1);
      pages.add(total - 2);
      pages.add(total - 3);
    }
    const sorted = Array.from(pages)
      .filter(function (p) {
        return p >= 1 && p <= total;
      })
      .sort(function (a, b) {
        return a - b;
      });

    const parts = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
        parts.push('ellipsis');
      }
      parts.push(sorted[i]);
    }
    return parts;
  }

  function updatePaginationUI(currentPage, trueTotalPages) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const paginationWrapper = container.querySelector('.pagination');
    if (!paginationWrapper) return;

    if (trueTotalPages <= 1) {
      paginationWrapper.classList.add('hidden');
      paginationWrapper.style.display = 'none';
      return;
    }

    paginationWrapper.classList.remove('hidden');
    paginationWrapper.style.display = '';

    const listUl = paginationWrapper.querySelector('.pagination__list');
    if (!listUl) return;

    const parts = computePaginationParts(currentPage, trueTotalPages);
    const liHtml = [];

    if (currentPage > 1) {
      liHtml.push(
        '<li><a href="' +
          buildPageHref(currentPage - 1) +
          '" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" data-mch-page="' +
          (currentPage - 1) +
          '" aria-label="Previous">' +
          CARET_LEFT_SVG +
          '</a></li>'
      );
    }

    parts.forEach(function (part) {
      if (part === 'ellipsis') {
        liHtml.push('<li><span class="pagination__item">...</span></li>');
      } else if (part === currentPage) {
        liHtml.push(
          '<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current" aria-current="page" aria-label="Page ' +
            part +
            '">' +
            part +
            '</a></li>'
        );
      } else {
        liHtml.push(
          '<li><a href="' +
            buildPageHref(part) +
            '" class="pagination__item link" data-mch-page="' +
            part +
            '" aria-label="Page ' +
            part +
            '">' +
            part +
            '</a></li>'
        );
      }
    });

    if (currentPage < trueTotalPages) {
      liHtml.push(
        '<li><a href="' +
          buildPageHref(currentPage + 1) +
          '" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" data-mch-page="' +
          (currentPage + 1) +
          '" aria-label="Next">' +
          CARET_RIGHT_SVG +
          '</a></li>'
      );
    }

    listUl.innerHTML = liHtml.join('');
  }

  function renderCachedPage(cacheEntry, requestedPage) {
    const listEl = document.getElementById('ProductsList');
    if (!listEl || !cacheEntry) return;

    const perPage = cacheEntry.perPage;
    const totalVisible = cacheEntry.visibleItems.length;
    const trueTotalPages = Math.max(1, Math.ceil(totalVisible / perPage));
    const effectivePage = Math.min(Math.max(1, requestedPage), trueTotalPages);

    const startIdx = (effectivePage - 1) * perPage;
    const pageSlice = cacheEntry.visibleItems.slice(startIdx, startIdx + perPage);
    const htmlBlocks = pageSlice.map(function (item) {
      return item.html;
    });

    if (effectivePage === 1 && cacheEntry.promoBlocks.length > 0) {
      cacheEntry.promoBlocks.forEach(function (promo) {
        const insertAt = Math.min(promo.index, htmlBlocks.length);
        htmlBlocks.splice(insertAt, 0, promo.html);
      });
    }

    listEl.innerHTML = htmlBlocks.join('');
    listEl.dataset.currentPage = String(effectivePage);
    listEl.dataset.mchVirtualReady = 'true';

    ensureInsertedCardsReady(listEl);
    syncEmptyState(totalVisible);
    updateProductCountDisplay(cacheEntry.totalHidden, pageSlice.length, totalVisible);
    updatePaginationUI(effectivePage, trueTotalPages);
  }

  async function fetchPageListElement(pageNum, sectionId) {
    const u = new URL(window.location.href, window.location.origin);
    u.searchParams.set('page', String(pageNum));
    if (sectionId) {
      u.searchParams.set('section_id', sectionId);
    }
    const res = await fetch(u.toString(), { credentials: 'same-origin' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.getElementById('ProductsList');
  }

  async function syncCollectionGrid() {
    const runId = ++activeRunId;
    const listEl = document.getElementById('ProductsList');
    if (!listEl) return;

    const perPage = parseInt(listEl.dataset.productsPerPage, 10) || 28;
    const currentPage = parseInt(listEl.dataset.currentPage, 10) || 1;
    const totalPages = parseInt(listEl.dataset.totalPages, 10) || 1;
    const sectionId = listEl.dataset.sectionId || '';

    // Capture original #ProductCount text whenever #ProductsList is freshly rendered by server/facets
    const productCountEl = document.getElementById('ProductCount');
    if (productCountEl && (!productCountEl.dataset.mchOriginalText || !listEl.dataset.mchProcessed)) {
      productCountEl.dataset.mchOriginalText = productCountEl.textContent;
    }
    listEl.dataset.mchProcessed = 'true';

    const currentExtracted = extractColumnsFromList(listEl);

    // Case 1: Single-page collection or search result
    if (totalPages <= 1) {
      const hiddenCount = currentExtracted.hiddenKeys.length;
      const visibleCount = currentExtracted.visibleItems.length;
      updateProductCountDisplay(hiddenCount, visibleCount, visibleCount);
      syncEmptyState(visibleCount);
      return;
    }

    // Case 2: Multi-page collection within full-scan threshold (<= 12 pages)
    if (totalPages <= MAX_FULL_SCAN_PAGES) {
      const cacheKey = getBaseCacheKey();
      if (pageCache.has(cacheKey)) {
        renderCachedPage(pageCache.get(cacheKey), currentPage);
        return;
      }

      // Immediately update count with current page's known hidden count while fetching remaining pages
      if (currentExtracted.hiddenKeys.length > 0) {
        updateProductCountDisplay(currentExtracted.hiddenKeys.length, currentExtracted.visibleItems.length);
      }

      const pageResults = new Array(totalPages + 1);
      pageResults[currentPage] = currentExtracted;

      const pagesToFetch = [];
      for (let p = 1; p <= totalPages; p++) {
        if (p !== currentPage) pagesToFetch.push(p);
      }

      let cursor = 0;
      let fetchFailed = false;
      async function worker() {
        while (cursor < pagesToFetch.length) {
          const p = pagesToFetch[cursor++];
          try {
            const remoteList = await fetchPageListElement(p, sectionId);
            if (runId !== activeRunId) return;
            pageResults[p] = extractColumnsFromList(remoteList);
          } catch (err) {
            fetchFailed = true;
          }
        }
      }

      const workers = [];
      const workerCount = Math.min(FETCH_CONCURRENCY, pagesToFetch.length);
      for (let w = 0; w < workerCount; w++) {
        workers.push(worker());
      }
      await Promise.all(workers);

      if (runId !== activeRunId || fetchFailed) return;

      const seenVisible = new Set();
      const seenHidden = new Set();
      const allVisible = [];
      let promoBlocks = [];

      for (let p = 1; p <= totalPages; p++) {
        const res = pageResults[p];
        if (!res) continue;
        if (p === 1 && res.promoBlocks.length > 0) {
          promoBlocks = res.promoBlocks;
        }
        res.visibleItems.forEach(function (item) {
          if (!seenVisible.has(item.key)) {
            seenVisible.add(item.key);
            allVisible.push(item);
          }
        });
        res.hiddenKeys.forEach(function (hKey) {
          seenHidden.add(hKey);
        });
      }

      const cacheEntry = {
        perPage: perPage,
        visibleItems: allVisible,
        totalHidden: seenHidden.size,
        promoBlocks: promoBlocks
      };
      pageCache.set(cacheKey, cacheEntry);
      renderCachedPage(cacheEntry, currentPage);
      return;
    }

    // Case 3: Very large collection (> 12 pages) — progressive lookahead backfill with cross-page deduplication
    const cacheKey = getBaseCacheKey();
    const storageKey = 'mch_pages_' + cacheKey;
    let storedPages = {};
    try {
      storedPages = JSON.parse(sessionStorage.getItem(storageKey) || '{}') || {};
    } catch (e) {
      storedPages = {};
    }

    const priorKeys = new Set();
    for (let p = 1; p < currentPage; p++) {
      if (Array.isArray(storedPages[p])) {
        storedPages[p].forEach(function (k) {
          priorKeys.add(k);
        });
      }
    }

    // Remove any visible items on currentPage that were already used to backfill an earlier page
    if (priorKeys.size > 0) {
      Array.from(listEl.children).forEach(function (col, idx) {
        if (!col.classList.contains('f-column') || col.classList.contains('image-column')) return;
        if (isColumnHiddenOos(col)) return;
        const key = getColumnKey(col, idx);
        if (priorKeys.has(key)) {
          col.remove();
        }
      });
    }

    const refreshedExtracted = extractColumnsFromList(listEl);
    const seenKeys = new Set(priorKeys);
    const currentPageKeys = [];
    refreshedExtracted.visibleItems.forEach(function (item) {
      seenKeys.add(item.key);
      currentPageKeys.push(item.key);
    });
    const seenHidden = new Set(refreshedExtracted.hiddenKeys);
    let visibleCount = refreshedExtracted.visibleItems.length;
    let nextPage = currentPage + 1;
    const maxLookaheadPage = Math.min(totalPages, currentPage + 6);

    while (visibleCount < perPage && nextPage <= maxLookaheadPage) {
      try {
        const remoteList = await fetchPageListElement(nextPage, sectionId);
        if (runId !== activeRunId) return;
        const extracted = extractColumnsFromList(remoteList);
        extracted.hiddenKeys.forEach(function (k) {
          seenHidden.add(k);
        });

        const tempContainer = document.createElement('div');
        extracted.visibleItems.forEach(function (item) {
          if (visibleCount < perPage && !seenKeys.has(item.key)) {
            seenKeys.add(item.key);
            currentPageKeys.push(item.key);
            tempContainer.innerHTML = item.html;
            const node = tempContainer.firstElementChild;
            if (node) {
              listEl.appendChild(node);
              visibleCount++;
            }
          }
        });
      } catch (err) {
        break;
      }
      nextPage++;
    }

    if (runId !== activeRunId) return;
    try {
      storedPages[currentPage] = currentPageKeys;
      sessionStorage.setItem(storageKey, JSON.stringify(storedPages));
    } catch (e) {}

    ensureInsertedCardsReady(listEl);
    updateProductCountDisplay(seenHidden.size, visibleCount);
  }

  // Intercept pagination clicks when virtual pagination cache is ready for instant switching
  document.addEventListener(
    'click',
    function (e) {
      const pageLink = e.target.closest('.pagination a.pagination__item[href]');
      if (!pageLink) return;

      const cacheKey = getBaseCacheKey();
      if (!pageCache.has(cacheKey)) return;

      try {
        const targetUrl = new URL(pageLink.href, window.location.origin);
        if (!targetUrl.searchParams.has('filter.v.availability') && targetUrl.pathname.includes('/collections/')) {
          targetUrl.searchParams.set('filter.v.availability', '1');
        }
        const targetPage =
          parseInt(pageLink.dataset.mchPage || targetUrl.searchParams.get('page') || '1', 10) || 1;

        e.preventDefault();
        e.stopPropagation();

        window.history.pushState({ mchVirtualPage: targetPage }, '', targetUrl.toString());
        renderCachedPage(pageCache.get(cacheKey), targetPage);

        const scrollTarget = document.getElementById('ProductGridWrapper') || document.getElementById('ProductGridContainer');
        if (scrollTarget) {
          const top = scrollTarget.getBoundingClientRect().top + window.pageYOffset - 100;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      } catch (err) {}
    },
    true
  );

  window.addEventListener('popstate', function () {
    const cacheKey = getBaseCacheKey();
    if (!pageCache.has(cacheKey)) return;
    const u = new URL(window.location.href, window.location.origin);
    const targetPage = parseInt(u.searchParams.get('page') || '1', 10) || 1;
    renderCachedPage(pageCache.get(cacheKey), targetPage);
  });

  // Intercept collection link clicks to preserve filter.v.availability=1
  document.addEventListener(
    'click',
    function (e) {
      const link = e.target.closest('a[href*="/collections/"]');
      if (!link || !link.href) return;
      if (link.closest('.pagination')) return;

      try {
        const url = new URL(link.href, window.location.origin);
        if (url.origin === window.location.origin && url.pathname.includes('/collections/')) {
          if (!url.searchParams.has('filter.v.availability')) {
            url.searchParams.set('filter.v.availability', '1');
            link.href = url.toString();
          }
        }
      } catch (err) {}
    },
    true
  );

  // Initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncCollectionGrid);
  } else {
    syncCollectionGrid();
  }

  // Theme rerender hooks (facets filter/sort AJAX updates & Shopify Theme Editor)
  document.addEventListener('collection:rerendered', syncCollectionGrid);
  document.addEventListener('shopify:section:load', function () {
    pageCache.clear();
    setTimeout(syncCollectionGrid, 60);
  });
  if (window.FoxTheme && window.FoxTheme.pubsub) {
    window.FoxTheme.pubsub.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, function () {
      setTimeout(syncCollectionGrid, 60);
    });
  }
})();